import { useRef, useState } from 'react';
import {
  Alert,
  Button,
  CloudUploadIcon,
  Field,
  Text,
  Textarea,
} from '@billda/design-system';
import SparkMD5 from 'spark-md5';
import { getErrorMessage } from '@billda/shared-ui';
import { useSnackbarStore } from '../../../../shared/stores/snackbarStore';
import {
  requestVersionUploadUrl,
  confirmVersionUpload,
  uploadFileToBlobWithProgress,
} from '../../api/filesApi';

type UploadState = 'idle' | 'fileSelected' | 'uploading';

interface VersionUploadAreaProps {
  fileId: string;
  contentType: string;
  onUploadComplete: () => void;
}

export function VersionUploadArea({
  fileId,
  contentType,
  onUploadComplete,
}: VersionUploadAreaProps) {
  const showSnackbar = useSnackbarStore(s => s.showSnackbar);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [state, setState] = useState<UploadState>('idle');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [notes, setNotes] = useState('');
  const [progress, setProgress] = useState(0);
  const [inlineError, setInlineError] = useState<string | null>(null);

  const resetState = () => {
    setState('idle');
    setSelectedFile(null);
    setNotes('');
    setProgress(0);
    setInlineError(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleFileSelect = (file: File) => {
    setSelectedFile(file);
    setState('fileSelected');
    setInlineError(null);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    const file = e.dataTransfer.files[0];
    if (file) {
      handleFileSelect(file);
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) {
      return;
    }

    setState('uploading');
    setProgress(0);
    setInlineError(null);

    try {
      // Compute MD5 hash for duplicate detection (matches Azure Blob Content-MD5)
      const buffer = await selectedFile.arrayBuffer();
      const contentHash = btoa(SparkMD5.ArrayBuffer.hash(buffer, true));

      const { uploadUrl, versionId } = await requestVersionUploadUrl(fileId, {
        contentType: selectedFile.type,
        fileSizeBytes: selectedFile.size,
        notes: notes || undefined,
        contentHash,
      });

      await uploadFileToBlobWithProgress(
        uploadUrl,
        selectedFile,
        selectedFile.type,
        (loaded, total) => {
          setProgress(Math.round((loaded / total) * 100));
        }
      );

      await confirmVersionUpload(fileId, { versionId });

      showSnackbar('New version uploaded', 'success');
      resetState();
      onUploadComplete();
    } catch (err: unknown) {
      const message = getErrorMessage(err);

      // Check for specific 409 errors
      const isAxiosError =
        err !== null &&
        typeof err === 'object' &&
        'response' in err &&
        typeof (err as { response?: unknown }).response === 'object';

      if (isAxiosError) {
        const response = (err as { response: { status: number } }).response;
        if (response.status === 409) {
          if (message.includes('identical')) {
            showSnackbar(
              'This file is identical to an existing version',
              'error'
            );
          } else {
            setInlineError(
              'A version upload is already in progress. Please wait and try again.'
            );
          }
          setState('idle');
          setSelectedFile(null);
          setNotes('');
          setProgress(0);
          if (fileInputRef.current) {
            fileInputRef.current.value = '';
          }
          return;
        }
      }

      showSnackbar(message, 'error');
      setState('fileSelected');
    }
  };

  return (
    <div data-testid="upload-new-version">
      <input
        type="file"
        ref={fileInputRef}
        hidden
        accept={contentType}
        onChange={e => {
          const file = e.target.files?.[0];
          if (file) {
            handleFileSelect(file);
          }
        }}
      />

      {inlineError && (
        <Alert severity="warning" className="mb-2">
          {inlineError}
        </Alert>
      )}

      {state === 'idle' && (
        <div
          onClick={() => fileInputRef.current?.click()}
          onDragOver={e => e.preventDefault()}
          onDrop={handleDrop}
          className="cursor-pointer rounded-xl border-2 border-dashed border-border-default px-4 py-6 text-center hover:border-border-focused hover:bg-surface-raised"
        >
          <CloudUploadIcon
            className="mb-1 size-8 text-content-secondary"
            aria-hidden="true"
          />
          <Text variant="body2" className="font-medium">
            Upload new version
          </Text>
          <Text variant="caption" color="secondary">
            Drop a file here or click to browse
          </Text>
        </div>
      )}

      {state === 'fileSelected' && selectedFile && (
        <div className="rounded-xl border border-border-default p-4">
          <Text variant="body2" className="mb-2 font-medium">
            {selectedFile.name}
          </Text>
          <Field label="Notes (optional)" className="mb-3">
            <Textarea
              value={notes}
              onChange={e => setNotes(e.target.value)}
              rows={2}
              maxLength={1000}
              data-testid="version-notes-input"
            />
          </Field>
          <div className="flex items-center gap-2">
            <Button
              variant="neutral"
              size="sm"
              onClick={handleUpload}
              data-testid="confirm-version-upload"
            >
              Upload
            </Button>
            <button
              type="button"
              className="cursor-pointer type-body-md text-content-primary underline hover:text-content-accent"
              onClick={resetState}
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {state === 'uploading' && selectedFile && (
        <div className="rounded-xl border border-border-default p-4">
          <Text variant="body2" className="mb-2 font-medium">
            {selectedFile.name}
          </Text>
          <div
            role="progressbar"
            aria-valuemin={0}
            aria-valuemax={100}
            aria-valuenow={progress}
            className="mb-1 h-1 w-full overflow-hidden rounded-full bg-surface-disabled"
          >
            <div
              className="h-full rounded-full bg-surface-primary transition-[width]"
              style={{ width: `${progress}%` }}
            />
          </div>
          <Text variant="caption" color="secondary">
            Uploading... {progress}%
          </Text>
        </div>
      )}
    </div>
  );
}
